import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Mic, MicOff, Send, Volume2, VolumeX, X, Mail, ChevronDown, ChevronRight, Inbox, CheckSquare, Clock, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXTS = {
  de: {
    greeting: 'Hey Sebastian! Ich bin Johanna. Was kann ich für dich tun?',
    placeholder: 'Sprich oder schreib mit Johanna...',
    listening: 'Höre zu...',
    thinking: 'Einen Moment...',
    micPermissionDenied: 'Mikrofon-Zugriff abgelehnt. In den Browser-Einstellungen aktivieren.',
    micNotSupported: 'Dein Browser unterstützt Voice nicht. Nutz bitte Chrome, Safari oder Edge.',
    loginRequired: 'Bitte erst im CRM einloggen.',
    error: 'Verbindungsproblem. Versuch nochmal.',
    examples: ['Was steht heute an?', 'Erinnere mich morgen um 10 an den Anruf', 'Was wissen wir über Müller GmbH?', 'Welche überfälligen Tasks habe ich?'],
  },
  en: {
    greeting: "Hey Sebastian! I'm Johanna. What can I do for you?",
    placeholder: 'Talk or type to Johanna...',
    listening: 'Listening...',
    thinking: 'One moment...',
    micPermissionDenied: 'Microphone access denied. Enable in browser settings.',
    micNotSupported: 'Your browser does not support voice. Please use Chrome, Safari, or Edge.',
    loginRequired: 'Please log in to the CRM first.',
    error: 'Connection issue. Try again.',
    examples: ['What is on for today?', 'Remind me tomorrow at 10 to call', 'What do we know about Müller GmbH?', 'What overdue tasks do I have?'],
  },
};

function detectLang(text) {
  if (!text) return 'de';
  const enWords = /\b(the|and|what|how|can|you|please|please|i|am|is|are|tomorrow|today|tasks?)\b/gi;
  const deWords = /\b(der|die|das|und|was|wie|kann|du|bitte|ich|bin|ist|sind|morgen|heute|aufgaben?)\b/gi;
  const en = (text.match(enWords) || []).length;
  const de = (text.match(deWords) || []).length;
  if (en > de + 1) return 'en';
  return 'de';
}

const SILENCE_WAIT_MS = 8000; // wait 8s of silence before sending voice input

export default function JohannaPage() {
  const [lang, setLang] = useState('de');
  const t = TEXTS[lang] || TEXTS.de;
  const [messages, setMessages] = useState([{ role: 'assistant', content: TEXTS.de.greeting }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  };
  const [voiceError, setVoiceError] = useState('');
  const [authToken, setAuthToken] = useState(null);
  const [transcript, setTranscript] = useState(''); // live transcript while listening
  const [countdown, setCountdown] = useState(0);    // silence countdown
  // Inbox state
  const [inboxOpen, setInboxOpen] = useState(false);
  const [mails, setMails] = useState([]);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailFilter, setMailFilter] = useState('unread'); // 'unread' | 'all' | 'important'
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyDraft, setReplyDraft] = useState(null);
  const [replyBusy, setReplyBusy] = useState(false);
  // Tasks state
  const [tasksOpen, setTasksOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);

  // Mitarbeiter laden (keine AI-Agents als Task-Assignees) — sobald authToken da ist
  useEffect(() => {
    if (!authToken) return;
    fetch(`${API}/auth/users`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const staff = (data || []).filter(u => ['admin', 'sales', 'customer_manager'].includes(u.role));
        setStaffOptions(staff);
      })
      .catch(() => setStaffOptions([]));
  }, [authToken]);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const finalTranscriptRef = useRef('');

  useEffect(() => {
    const tok = localStorage.getItem('crm_token');
    setAuthToken(tok);
  }, []);

  // Helper: bei 401 zurück zum CRM-Login redirecten
  const handle401 = (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('crm_token');
      window.location.href = '/crm';
      return true;
    }
    return false;
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const loadInbox = async (filter = mailFilter) => {
    if (!authToken) return;
    setMailLoading(true);
    try {
      const { data } = await axios.get(`${API}/johanna/inbox?limit=20&filter_mode=${filter}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setMails(data || []);
    } catch (err) {
      setMails([]);
      if (handle401(err)) return;
      setVoiceError(err.response?.data?.detail || 'Inbox-Zugriff fehlgeschlagen — vermutlich muss OAuth-Token mit Mail-Read-Scopes neu autorisiert werden.');
    }
    setMailLoading(false);
  };

  const openThread = async (threadId) => {
    setSelectedThread({ loading: true, id: threadId, messages: [] });
    try {
      const { data } = await axios.get(`${API}/johanna/inbox/thread/${threadId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSelectedThread(data);
    } catch (err) {
      setSelectedThread(null);
      setVoiceError('Thread konnte nicht geladen werden.');
    }
  };

  const draftReplyForThread = async (instruction = '') => {
    if (!selectedThread || !selectedThread.messages?.length) return;
    setReplyBusy(true);
    try {
      const last = selectedThread.messages[selectedThread.messages.length - 1];
      const { data } = await axios.post(`${API}/johanna/inbox/draft-reply`, {
        thread_id: selectedThread.id, message_id: last.id, instruction,
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      setReplyDraft(data);
    } catch (err) {
      setVoiceError(err.response?.data?.detail || 'Draft fehlgeschlagen');
    }
    setReplyBusy(false);
  };

  const sendReply = async () => {
    if (!replyDraft) return;
    setReplyBusy(true);
    try {
      await axios.post(`${API}/johanna/inbox/send`, replyDraft, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setReplyDraft(null);
      setSelectedThread(null);
      loadInbox();
    } catch (err) {
      setVoiceError(err.response?.data?.detail || 'Versand fehlgeschlagen');
    }
    setReplyBusy(false);
  };

  const archiveMail = async (messageId) => {
    try {
      await axios.post(`${API}/johanna/inbox/${messageId}/mark`, {
        remove_labels: ['INBOX'],
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      setMails(prev => prev.filter(m => m.id !== messageId));
      if (selectedThread && selectedThread.id) {
        const stillHas = selectedThread.messages.some(m => m.id === messageId);
        if (stillHas) setSelectedThread(null);
      }
    } catch (err) { setVoiceError('Archive fehlgeschlagen'); }
  };

  // Auto-load inbox when first opened
  useEffect(() => {
    if (inboxOpen && mails.length === 0 && !mailLoading && authToken) {
      loadInbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxOpen, authToken]);

  const loadTasks = async () => {
    if (!authToken) return;
    setTasksLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks?status=open,in_progress,waiting`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      // Sort by priority + due_at
      const prio = { urgent: 0, high: 1, normal: 2, low: 3 };
      data.sort((a, b) => {
        const pa = prio[a.priority] ?? 2;
        const pb = prio[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
        if (a.due_at && b.due_at) return new Date(a.due_at) - new Date(b.due_at);
        if (a.due_at) return -1;
        if (b.due_at) return 1;
        return 0;
      });
      setTasks(data);
    } catch { setTasks([]); }
    setTasksLoading(false);
  };

  const completeTaskInline = async (id) => {
    try {
      await axios.post(`${API}/tasks/${id}/complete`, {}, { headers: { Authorization: `Bearer ${authToken}` } });
      loadTasks();
    } catch (err) { setVoiceError('Fehler beim Abhaken'); }
  };

  const reassignTask = async (id, assigneeLabel) => {
    try {
      await axios.patch(`${API}/tasks/${id}`, { assignee_label: assigneeLabel }, { headers: { Authorization: `Bearer ${authToken}` } });
      loadTasks();
    } catch (err) { setVoiceError('Fehler beim Neuzuweisen'); }
  };

  // Auto-load tasks when opened
  useEffect(() => {
    if (tasksOpen && authToken) loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksOpen, authToken]);

  // Prime SpeechSynthesis voices on mount (Chrome/Edge laden sie async)
  useEffect(() => {
    if (!window.speechSynthesis) return;
    // trigger voices load
    window.speechSynthesis.getVoices();
    const handler = () => window.speechSynthesis.getVoices();
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', handler);
  }, []);

  // Speak text with browser SpeechSynthesis (mit Voices-Loading-Schutz)
  const speak = (text) => {
    if (muted || !window.speechSynthesis || !text) return;

    const doSpeak = () => {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      const detected = detectLang(text);
      utter.lang = detected === 'en' ? 'en-US' : 'de-DE';
      const voices = window.speechSynthesis.getVoices();
      // Bevorzuge native Stimmen für die Sprache
      let v = voices.find(x => x.lang === (detected === 'en' ? 'en-US' : 'de-DE') && x.localService);
      if (!v) v = voices.find(x => x.lang.startsWith(detected === 'en' ? 'en' : 'de'));
      if (v) utter.voice = v;
      utter.rate = 1.05;
      utter.pitch = 1.0;
      utter.volume = 1.0;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utter);
    };

    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      // Voices noch nicht geladen — warten
      const onVoices = () => {
        window.speechSynthesis.removeEventListener('voiceschanged', onVoices);
        doSpeak();
      };
      window.speechSynthesis.addEventListener('voiceschanged', onVoices);
      // Trigger Load
      window.speechSynthesis.getVoices();
      // Fallback wenn voiceschanged nie kommt
      setTimeout(() => {
        window.speechSynthesis.removeEventListener('voiceschanged', onVoices);
        if (window.speechSynthesis.getVoices().length > 0) doSpeak();
      }, 800);
    } else {
      doSpeak();
    }
  };

  const send = async (text) => {
    const clean = (text || '').trim();
    if (!clean || loading) return;
    if (!authToken) {
      setVoiceError(t.loginRequired);
      return;
    }
    const newMsg = { role: 'user', content: clean };
    setMessages(prev => [...prev, newMsg]);
    setInput('');
    setLoading(true);
    setVoiceError('');

    // Update UI language based on user input
    const detected = detectLang(clean);
    if (detected !== lang) setLang(detected);

    try {
      const { data } = await axios.post(`${API}/johanna/chat`, {
        message: clean,
        history: messages.slice(-8),
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      const reply = data.reply || '...';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      speak(reply);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('crm_token');
        setMessages(prev => [...prev, { role: 'assistant', content: t.loginRequired }]);
        setTimeout(() => { window.location.href = '/crm'; }, 1500);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: t.error }]);
      }
    }
    setLoading(false);
  };

  // Clear all timers
  const clearTimers = () => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
    setCountdown(0);
  };

  // Start a fresh 8s silence timer — fires send when expires
  const startSilenceTimer = () => {
    clearTimers();
    let secsLeft = SILENCE_WAIT_MS / 1000;
    setCountdown(secsLeft);
    countdownIntervalRef.current = setInterval(() => {
      secsLeft -= 1;
      setCountdown(secsLeft);
      if (secsLeft <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, 1000);
    silenceTimerRef.current = setTimeout(() => {
      // Send what we have
      const finalText = finalTranscriptRef.current.trim();
      try { recognitionRef.current?.stop(); } catch {}
      setListening(false);
      clearTimers();
      finalTranscriptRef.current = '';
      setTranscript('');
      if (finalText) send(finalText);
    }, SILENCE_WAIT_MS);
  };

  // Start voice input
  const startListening = () => {
    setVoiceError('');
    setTranscript('');
    finalTranscriptRef.current = '';
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceError(t.micNotSupported);
      return;
    }
    const recog = new SR();
    recog.lang = lang === 'en' ? 'en-US' : 'de-DE';
    recog.continuous = true;        // läuft weiter über Pausen hinweg
    recog.interimResults = true;    // gibt auch unfertige Ergebnisse zurück
    recog.maxAlternatives = 1;

    recog.onstart = () => {
      setListening(true);
      startSilenceTimer();
    };

    recog.onresult = (e) => {
      // Reset 8-Sek-Timer bei jedem neuen Spracherkennungs-Event
      startSilenceTimer();
      // Build cumulative final transcript
      let finalText = '';
      let interim = '';
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      finalTranscriptRef.current = finalText;
      setTranscript((finalText + interim).trim());
    };

    recog.onerror = (e) => {
      setListening(false);
      clearTimers();
      if (e.error === 'not-allowed') setVoiceError(t.micPermissionDenied);
      else if (e.error !== 'no-speech' && e.error !== 'aborted') setVoiceError(`Mic-Error: ${e.error}`);
    };

    recog.onend = () => {
      // Wenn der Browser von selbst beendet (z.B. Stille) NICHT direkt schließen —
      // wir warten auf den 8-Sek-Timer. Außer der User hat manuell gestoppt.
      if (!silenceTimerRef.current && listening) {
        setListening(false);
        clearTimers();
      }
    };

    recognitionRef.current = recog;
    try { recog.start(); } catch (err) {}
  };

  const stopListening = () => {
    // User klickt zum Beenden — sofort senden, kein Warten
    clearTimers();
    const finalText = (finalTranscriptRef.current + ' ' + transcript).trim();
    finalTranscriptRef.current = '';
    setTranscript('');
    if (recognitionRef.current) try { recognitionRef.current.stop(); } catch {}
    setListening(false);
    if (finalText) send(finalText);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#1f1f1f] flex items-center justify-between sticky top-0 bg-[#0a0a0a] z-10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#befa0a] flex items-center justify-center text-[#0a0a0a] font-bold">J</div>
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-[#befa0a]" style={{ fontFamily: "'Oswald', sans-serif" }}>Johanna</p>
            <p className="text-[10px] text-white/40">Persönliche Assistentin</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTasksOpen(o => !o)} title="Tasks"
            className={`p-2 transition-colors ${tasksOpen ? 'text-[#befa0a]' : 'text-white/50 hover:text-white'}`}>
            <CheckSquare size={18} />
          </button>
          <button onClick={() => setInboxOpen(o => !o)} title="Inbox"
            className={`p-2 transition-colors ${inboxOpen ? 'text-[#befa0a]' : 'text-white/50 hover:text-white'}`}>
            <Inbox size={18} />
          </button>
          {speaking && (
            <button onClick={stopSpeaking} title="Audio jetzt stoppen"
              className="px-2 py-1 flex items-center gap-1 bg-red-500/20 text-red-300 hover:bg-red-500/40 transition-colors rounded text-xs font-bold uppercase tracking-wider animate-pulse"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Square size={12} fill="currentColor" /> Stop
            </button>
          )}
          <button onClick={() => { setMuted(m => !m); if (!muted) stopSpeaking(); }}
            title={muted ? 'Audio einschalten' : 'Audio dauerhaft stummschalten'}
            className={`px-2 py-1 flex items-center gap-1 transition-colors rounded text-xs font-bold uppercase tracking-wider ${
              muted ? 'bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/40' : 'text-white/50 hover:text-white'
            }`}
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {muted ? <><VolumeX size={14} /> Stumm</> : <><Volume2 size={14} /> Audio</>}
          </button>
          <a href="/crm" className="text-white/40 hover:text-white p-2"><X size={20} /></a>
        </div>
      </div>

      {/* Inbox Panel (einklappbar) */}
      {inboxOpen && (
        <div className="border-b border-[#1f1f1f] bg-[#0d0d0d] max-h-[60vh] overflow-y-auto">
          <div className="px-4 py-2 flex items-center justify-between sticky top-0 bg-[#0d0d0d] border-b border-[#1f1f1f]">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-[#befa0a]" />
              <span className="text-xs font-bold uppercase tracking-wider text-white/70">Inbox</span>
              <div className="flex gap-1 ml-2">
                {['unread', 'all', 'important'].map(f => (
                  <button key={f} onClick={() => { setMailFilter(f); loadInbox(f); }}
                    className={`text-[10px] uppercase px-2 py-0.5 rounded ${mailFilter === f ? 'bg-[#befa0a] text-[#0a0a0a]' : 'text-white/40 hover:text-white'}`}>
                    {f === 'unread' ? 'Ungelesen' : f === 'important' ? 'Wichtig' : 'Alle'}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => loadInbox()} disabled={mailLoading} className="text-xs text-white/40 hover:text-white">
              {mailLoading ? '...' : '↻ Reload'}
            </button>
          </div>
          {mailLoading ? (
            <p className="px-4 py-4 text-sm text-white/40">Lade Inbox...</p>
          ) : mails.length === 0 ? (
            <p className="px-4 py-4 text-sm text-white/40">Keine Mails.</p>
          ) : (
            <div className="divide-y divide-[#1f1f1f]">
              {mails.map(m => {
                const sender = (m.from || '').split('<')[0].trim() || m.from;
                const catColor = {
                  wichtig: 'bg-red-500/20 text-red-300',
                  action: 'bg-[#befa0a]/20 text-[#befa0a]',
                  info: 'bg-blue-500/20 text-blue-300',
                  spam: 'bg-gray-500/20 text-gray-400',
                }[m.category] || 'bg-gray-500/20 text-gray-400';
                return (
                  <div key={m.id} onClick={() => openThread(m.thread_id)}
                    className="px-4 py-2.5 hover:bg-[#161616] cursor-pointer">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-xs text-white/90 truncate flex-1">{sender}</span>
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${catColor}`}>{m.category || 'info'}</span>
                    </div>
                    <p className="text-xs text-white/70 truncate font-medium">{m.subject}</p>
                    <p className="text-[10px] text-white/40 truncate">{m.snippet}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tasks Panel (einklappbar) */}
      {tasksOpen && (
        <div className="border-b border-[#1f1f1f] bg-[#0d0d0d] max-h-[60vh] overflow-y-auto">
          <div className="px-4 py-2 flex items-center justify-between sticky top-0 bg-[#0d0d0d] border-b border-[#1f1f1f]">
            <div className="flex items-center gap-2">
              <CheckSquare size={14} className="text-[#befa0a]" />
              <span className="text-xs font-bold uppercase tracking-wider text-white/70">Offene Tasks</span>
              <span className="text-[10px] text-white/40">({tasks.length})</span>
            </div>
            <button onClick={() => loadTasks()} disabled={tasksLoading} className="text-xs text-white/40 hover:text-white">
              {tasksLoading ? '...' : '↻ Reload'}
            </button>
          </div>
          {tasksLoading ? (
            <p className="px-4 py-4 text-sm text-white/40">Lade Tasks...</p>
          ) : tasks.length === 0 ? (
            <p className="px-4 py-4 text-sm text-white/40">Keine offenen Tasks. 🎉</p>
          ) : (
            <div className="divide-y divide-[#1f1f1f]">
              {tasks.map(t => {
                const due = t.due_at ? new Date(t.due_at) : null;
                const now = new Date();
                const isOverdue = due && due < now;
                const isToday = due && due.toDateString() === now.toDateString();
                const prioColor = {
                  urgent: 'bg-red-500',
                  high: 'bg-orange-400',
                  normal: 'bg-gray-500',
                  low: 'bg-gray-700',
                }[t.priority] || 'bg-gray-500';
                return (
                  <div key={t.id} className="px-4 py-2.5 hover:bg-[#161616] flex items-start gap-2">
                    <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${prioColor}`} />
                    <button onClick={() => completeTaskInline(t.id)}
                      className="mt-0.5 w-4 h-4 rounded border-2 border-white/30 hover:border-[#befa0a] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white/90 truncate">{t.title}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[10px]">
                        {due && (
                          <span className={isOverdue ? 'text-red-400 font-bold' : isToday ? 'text-[#befa0a] font-bold' : 'text-white/40'}>
                            <Clock size={9} className="inline mr-0.5" />
                            {isOverdue ? 'Überfällig ' : isToday ? 'Heute ' : ''}
                            {due.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                            {' '}{due.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {t.assignee_label && (
                          <span className="text-white/50 px-1.5 py-0.5 bg-[#1f1f1f] rounded">→ {t.assignee_label}</span>
                        )}
                        {t.lead && (
                          <span className="text-[#658a07]">· {t.lead.company_name || 'Lead'}</span>
                        )}
                      </div>
                    </div>
                    <select value={t.assignee_label || ''} onChange={e => reassignTask(t.id, e.target.value)}
                      className="text-[10px] bg-[#1a1a1a] border border-[#252525] text-white/70 rounded px-1 py-0.5 max-w-[120px]">
                      <option value="">— Wer? —</option>
                      {staffOptions.map(u => (
                        <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Thread + Reply Modal */}
      {selectedThread && (
        <div className="fixed inset-0 z-50 flex" onClick={() => { setSelectedThread(null); setReplyDraft(null); }}>
          <div className="flex-1 bg-black/70" />
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-2xl bg-[#0a0a0a] h-full overflow-y-auto border-l border-[#1f1f1f] flex flex-col">
            <div className="px-4 py-3 border-b border-[#1f1f1f] flex items-center justify-between sticky top-0 bg-[#0a0a0a] z-10">
              <span className="text-sm font-bold text-white/80 uppercase tracking-wider">E-Mail-Thread</span>
              <button onClick={() => { setSelectedThread(null); setReplyDraft(null); }} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            {selectedThread.loading ? (
              <p className="p-4 text-white/40 text-sm">Lade...</p>
            ) : (
              <div className="flex-1 p-4 space-y-3">
                {selectedThread.messages.map((m, i) => (
                  <div key={m.id} className="border border-[#1f1f1f] rounded p-3 bg-[#0d0d0d]">
                    <p className="text-xs text-white/50 mb-1"><strong>Von:</strong> {m.from}</p>
                    <p className="text-xs text-white/50 mb-2"><strong>An:</strong> {m.to}</p>
                    <p className="text-sm text-white/90 font-bold mb-2">{m.subject}</p>
                    <p className="text-sm text-white/70 whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
                {!replyDraft && (
                  <div className="flex gap-2">
                    <button onClick={() => draftReplyForThread('')} disabled={replyBusy}
                      className="bg-[#befa0a] text-[#0a0a0a] px-3 py-2 text-xs font-bold uppercase rounded">
                      {replyBusy ? 'Generiert...' : 'Antwort entwerfen'}
                    </button>
                    <button onClick={() => archiveMail(selectedThread.messages[selectedThread.messages.length - 1]?.id)}
                      className="bg-[#1a1a1a] border border-[#252525] text-white/70 px-3 py-2 text-xs font-bold uppercase rounded">
                      Archivieren
                    </button>
                  </div>
                )}
                {replyDraft && (
                  <div className="border border-[#befa0a]/40 rounded p-3 bg-[#befa0a]/5 mt-3">
                    <p className="text-[10px] uppercase tracking-wider text-[#befa0a] font-bold mb-2">Antwort-Entwurf</p>
                    <p className="text-xs text-white/50 mb-1"><strong>An:</strong> {replyDraft.to}</p>
                    <input value={replyDraft.subject || ''} onChange={e => setReplyDraft(d => ({ ...d, subject: e.target.value }))}
                      className="w-full bg-[#161616] border border-[#252525] text-white text-sm p-2 mb-2 rounded" />
                    <textarea value={replyDraft.body || ''} onChange={e => setReplyDraft(d => ({ ...d, body: e.target.value }))}
                      rows={8}
                      className="w-full bg-[#161616] border border-[#252525] text-white text-sm p-2 rounded font-mono" />
                    <div className="flex gap-2 mt-2">
                      <button onClick={sendReply} disabled={replyBusy}
                        className="bg-[#befa0a] text-[#0a0a0a] px-3 py-2 text-xs font-bold uppercase rounded">
                        {replyBusy ? 'Sendet...' : 'Senden'}
                      </button>
                      <button onClick={() => setReplyDraft(null)} className="text-white/40 text-xs">Verwerfen</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat */}
      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-2xl mx-auto w-full space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap rounded-2xl ${
              m.role === 'user' ? 'bg-[#befa0a] text-[#0a0a0a] font-medium' : 'bg-[#1a1a1a] text-white/90 border border-[#252525]'
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#1a1a1a] border border-[#252525] px-4 py-2.5 text-white/40 text-sm rounded-2xl">
              <span className="animate-pulse">{t.thinking}</span>
            </div>
          </div>
        )}
        {voiceError && (
          <div className="flex justify-center">
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 text-xs rounded">{voiceError}</div>
          </div>
        )}

        {/* Quick examples (only initial) */}
        {messages.length === 1 && (
          <div className="pt-4">
            <p className="text-[10px] uppercase tracking-wider text-white/30 mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Beispiele</p>
            <div className="grid grid-cols-1 gap-2">
              {t.examples.map((ex, i) => (
                <button key={i} onClick={() => send(ex)}
                  className="text-left text-xs px-3 py-2 border border-[#252525] text-white/60 rounded hover:border-[#befa0a]/50 hover:text-white">
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Live transcript while listening */}
      {listening && (
        <div className="px-4 py-2 bg-[#1a1a1a] border-t border-[#252525]">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-[#befa0a] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.listening} · sende in {countdown}s
              </p>
              <p className="text-sm text-white/80 truncate">{transcript || '...'}</p>
            </div>
            <button onClick={stopListening}
              className="bg-[#befa0a] text-[#0a0a0a] px-3 py-1.5 text-xs font-bold uppercase rounded"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Jetzt senden
            </button>
          </div>
        </div>
      )}

      {/* Input + Voice */}
      <div className="px-4 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]">
        <div className="max-w-2xl mx-auto flex items-end gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey}
            placeholder={listening ? t.listening : t.placeholder}
            disabled={loading || listening}
            className="flex-1 bg-[#161616] border border-[#252525] rounded-full text-white text-sm px-4 py-3 focus:outline-none focus:border-[#befa0a] placeholder-white/30 disabled:opacity-50"
            style={{ fontSize: '16px' }} />
          <button onClick={listening ? stopListening : startListening}
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              listening ? 'bg-red-500 animate-pulse text-white' : 'bg-[#befa0a] text-[#0a0a0a] hover:bg-white'
            }`}>
            {listening ? <MicOff size={20} /> : <Mic size={20} />}
          </button>
          {input.trim() && !listening && (
            <button onClick={() => send(input)} disabled={loading}
              className="flex-shrink-0 w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#befa0a] text-[#befa0a] flex items-center justify-center hover:bg-[#befa0a] hover:text-[#0a0a0a]">
              <Send size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
